import React, { useState, useEffect, useRef } from 'react';
import { MdMenu, MdClose, MdPublic } from 'react-icons/md';
import Logo from '../assets/logo.png';
import { getStaticContent } from '../constants/staticContent';
import { speak, SLOGAN } from '../utils/speak';

const MainNav = ({ language = 'en', onLanguageChange, onNavigate, highlightLanguage = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [langDropdown, setLangDropdown] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef(null);

  /* Scroll shadow */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* Close language dropdown on outside click */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setLangDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /* ESC key support */
  useEffect(() => {
    const handleEsc = (e) => e.key === 'Escape' && setLangDropdown(false);
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  const navLabels = getStaticContent(language).NAV.menu;
  const menuHrefs = ['#home', '#why', '#gallery', '#usage', '#downloads', '#about', '#services'];
  const menuItems = menuHrefs.map((href, i) => ({ label: navLabels[i], href }));

  const handleMenuClick = (href) => {
    onNavigate?.(href.replace('#', ''));
    setIsOpen(false);
  };

  return (
    <>
      {/* Desktop underline animation */}
      <style>{`
        .nav-item {
          position: relative;
          transition: color 0.25s ease;
        }
        .nav-item::after {
          content: '';
          position: absolute;
          left: 0;
          bottom: -6px;
          width: 0;
          height: 2px;
          background: linear-gradient(90deg, #2d5016, #85BF35);
          transition: width 0.25s ease;
        }
        .nav-item:hover::after {
          width: 100%;
        }

        /* Attention ring while the welcome message is being spoken */
        @keyframes langPulse {
          0%   { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.55); }
          70%  { box-shadow: 0 0 0 10px rgba(16, 185, 129, 0); }
          100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
        }
        .lang-speaking {
          animation: langPulse 1.4s ease-out infinite;
          border-color: #10b981 !important;
          background-color: #ecfdf5;
        }
      `}</style>

      {/* Navbar */}
      <header
        className={`
          sticky top-0 z-50 w-full bg-white
          transition-shadow duration-300
          ${scrolled ? 'shadow-md' : 'shadow-sm'}
        `}
      >
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex h-16 items-center justify-between">

            {/* Logo + Text — click returns to home / top */}
            <button
              type="button"
              onClick={() => {
                speak(SLOGAN.text, SLOGAN.lang);
                handleMenuClick('#home');
              }}
              aria-label="Go to home"
              className="flex items-center gap-2 sm:gap-3 flex-shrink-0 rounded-lg
                         
                         hover:opacity-90 transition"
            >
              <img
                src={Logo}
                alt="CropShield Logo"
                className="w-10 h-10 sm:w-11 sm:h-11 object-contain"
              />
              <span className="text-lg sm:text-xl font-bold text-emerald-900 whitespace-nowrap">
                CropShield
              </span>
            </button>

            {/* Desktop Menu */}
            <nav className="hidden lg:flex items-center gap-8">
              {menuItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={(e) => {
                    e.preventDefault();
                    handleMenuClick(item.href);
                  }}
                  className="nav-item text-sm font-semibold text-gray-700 hover:text-emerald-900"
                >
                  {item.label}
                </a>
              ))}
            </nav>

            {/* Right Controls */}
            <div className="flex items-center gap-2 sm:gap-3">

              {/* Language Selector */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setLangDropdown((v) => !v)}
                  className={`
                    flex items-center gap-2
                    h-9 px-3 rounded-lg
                    text-sm font-semibold text-gray-700
                    border border-gray-200
                    hover:bg-emerald-50 hover:border-emerald-300
                    focus:outline-none focus:ring-2 focus:ring-emerald-300
                    transition
                    ${highlightLanguage ? 'lang-speaking' : ''}
                  `}
                >
                  <MdPublic className="w-4 h-4" />
                  <span className="hidden sm:inline uppercase">
                    {language}
                  </span>
                </button>

                {langDropdown && (
                  <div className="
                    absolute right-0 mt-2 w-44
                    rounded-xl bg-white
                    border border-gray-200
                    shadow-xl z-50
                  ">
                    {[
                      { code: 'en', label: 'English' },
                      { code: 'hi', label: 'हिन्दी' },
                      { code: 'gu', label: 'ગુજરાતી' },
                    ].map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          onLanguageChange(lang.code);
                          setLangDropdown(false);
                        }}
                        className={`
                          w-full px-4 py-2.5 text-sm text-left
                          flex items-center justify-between
                          transition
                          ${
                            language === lang.code
                              ? 'bg-emerald-100 text-emerald-900 font-semibold'
                              : 'text-gray-700 hover:bg-emerald-50'
                          }
                        `}
                      >
                        {lang.label}
                        {language === lang.code && (
                          <span className="text-emerald-700 text-xs">●</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Hamburger */}
              <button
                className="
                  lg:hidden h-9 w-9
                  flex items-center justify-center
                  rounded-lg
                  hover:bg-emerald-50
                  transition
                "
                onClick={() => setIsOpen((v) => !v)}
                aria-label="Toggle menu"
              >
                {isOpen ? <MdClose size={22} /> : <MdMenu size={22} />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isOpen && (
            <div className="lg:hidden absolute top-full left-0 w-full bg-white border-t shadow-md z-40">
              <div className="px-4 py-3 space-y-1">
                {menuItems.map((item) => (
                  <button
                    key={item.href}
                    onClick={() => handleMenuClick(item.href)}
                    className="
                      w-full text-left px-4 py-3 rounded-lg
                      text-sm font-semibold text-gray-700
                      hover:bg-emerald-50 hover:text-emerald-900
                      transition
                    "
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </header>
    </>
  );
};

export default MainNav;
